/**
 * ============================================================================
 * KbExtranetInviteUserDialog — Modal para o admin do tenant registrar um novo
 * usuário externo da Extranet KB.
 *
 * Modelo (alinhado ao "Novo Afiliado"):
 *   - O admin apenas registra os dados do usuário (nome, e-mail, organização,
 *     tipo). NENHUMA conta é criada no Auth e NENHUM link/token é gerado.
 *   - O próprio dono do e-mail conclui o cadastro na página pública
 *     `/extranet/{slug}/cadastro`, onde define a senha. O signup público
 *     "adota" o pré-cadastro pelo e-mail.
 *   - Isso elimina a vulnerabilidade de account takeover: o admin nunca toca
 *     em credenciais de terceiros.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-10 v2.1.0 - Layout premium (header em degradê + FormModalSection
 *                                + rodapé fixo), alinhado ao GrantAccessDialog.
 *   RELEASE: 2026-07-10 v2.0.0 - Refatorado como pré-cadastro (sem Auth/link).
 *   RELEASE: 2026-07-09 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Copy,
  Loader2,
  ShieldCheck,
  User as UserIcon,
  UserPlus,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InlineNotice } from "@/components/ui/inline-notice";
import { FormModalSection } from "@/components/shared/FormModalSection";

type ExternalType = "customer" | "partner" | "vendor";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function KbExtranetInviteUserDialog({ open, onOpenChange }: Props) {
  const { currentTenant } = useAuth();
  const qc = useQueryClient();

  // ----- estado do formulário -----
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [organization, setOrganization] = useState("");
  const [externalType, setExternalType] = useState<ExternalType>("customer");
  const [submitting, setSubmitting] = useState(false);
  // URL pública de cadastro devolvida pelo backend após o pré-cadastro.
  const [signupUrl, setSignupUrl] = useState<string | null>(null);

  const reset = () => {
    setEmail("");
    setDisplayName("");
    setOrganization("");
    setExternalType("customer");
    setSignupUrl(null);
  };

  const closeAll = () => {
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!currentTenant?.slug) {
      toast({ title: "Tenant não identificado", variant: "destructive" });
      return;
    }
    // Validações client-side leves (o backend também valida).
    const cleanEmail = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleanEmail)) {
      toast({ title: "E-mail inválido", variant: "destructive" });
      return;
    }
    if (!displayName.trim()) {
      toast({ title: "Informe o nome do usuário", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    setSignupUrl(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        "kb-extranet-admin-invite",
        {
          body: {
            tenant_slug: currentTenant.slug,
            email: cleanEmail,
            display_name: displayName.trim(),
            organization: organization.trim() || undefined,
            external_type: externalType,
            redirect_base_url: window.location.origin,
          },
        },
      );

      if (error) {
        logger.error("[KbExtranetInviteUserDialog] invoke error", error);
        toast({
          title: "Falha ao registrar usuário",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      if (!data?.success) {
        toast({
          title: "Falha ao registrar usuário",
          description: data?.error ?? "Erro desconhecido",
          variant: "destructive",
        });
        return;
      }

      // Sucesso — atualiza listagens e mostra a URL pública para o admin
      // repassar ao usuário pelo canal que preferir.
      qc.invalidateQueries({ queryKey: ["kb-admin-extranet-users"] });
      toast({
        title: "Usuário registrado",
        description:
          "Compartilhe o link do portal para que o usuário conclua o cadastro.",
      });
      setSignupUrl(data.signup_url ?? null);
    } catch (err) {
      logger.error("[KbExtranetInviteUserDialog] erro inesperado", err);
      toast({
        title: "Erro inesperado",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const copySignupUrl = async () => {
    if (!signupUrl) return;
    try {
      await navigator.clipboard.writeText(signupUrl);
      toast({ title: "Link copiado" });
    } catch {
      toast({ title: "Não foi possível copiar", variant: "destructive" });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      {/* Shell premium: header em degradê + corpo rolável + rodapé fixo
          (mesmo padrão do GrantAccessDialog). */}
      <DialogContent className="max-w-2xl w-[95vw] h-[90dvh] md:h-auto md:max-h-[85dvh] flex flex-col p-0 gap-0 overflow-hidden sm:rounded-lg">
        {/* Capa premium padronizada */}
        <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent shrink-0">
          <div className="flex items-start gap-3 min-w-0 pr-8">
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg shrink-0">
              <UserPlus className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
            <div className="flex flex-col min-w-0">
              <DialogTitle className="truncate">Novo usuário externo</DialogTitle>
              <DialogDescription className="text-xs line-clamp-2 sm:line-clamp-none">
                Registre um usuário externo. O próprio usuário finalizará o
                cadastro definindo a senha na página pública da Extranet.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Corpo rolável com seções padronizadas */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {/* Aviso de segurança — reforça que o admin nunca recebe credenciais. */}
          <FormModalSection icon={ShieldCheck} title="Segurança">
            <InlineNotice variant="info" icon={ShieldCheck}>
              Por segurança, o link de acesso e a senha nunca chegam ao admin.
              Somente o dono do e-mail cria a própria senha ao concluir o
              cadastro.
            </InlineNotice>
          </FormModalSection>

          {/* Identificação do usuário externo */}
          <FormModalSection icon={UserIcon} title="Identificação">
            <div className="space-y-1.5">
              <Label htmlFor="ext-email">E-mail *</Label>
              <Input
                id="ext-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@empresa.com.br"
                maxLength={254}
                disabled={submitting || !!signupUrl}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ext-name">Nome de exibição *</Label>
              <Input
                id="ext-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Ex.: João da Silva"
                maxLength={120}
                disabled={submitting || !!signupUrl}
              />
            </div>
          </FormModalSection>

          {/* Classificação: organização (texto livre) + tipo do vínculo */}
          <FormModalSection icon={Building2} title="Classificação">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="ext-org">Organização</Label>
                <Input
                  id="ext-org"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  placeholder="Empresa / cliente"
                  maxLength={120}
                  disabled={submitting || !!signupUrl}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select
                  value={externalType}
                  onValueChange={(v) => setExternalType(v as ExternalType)}
                  disabled={submitting || !!signupUrl}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Cliente</SelectItem>
                    <SelectItem value="partner">Parceiro</SelectItem>
                    <SelectItem value="vendor">Fornecedor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </FormModalSection>

          {/* Após o registro, mostramos o link público para o admin repassar. */}
          {signupUrl && (
            <FormModalSection icon={Copy} title="Link do portal">
              <InlineNotice variant="success">
                <div className="space-y-2">
                  <p className="text-sm">
                    Compartilhe este link com o usuário para que ele conclua o
                    cadastro e defina a própria senha:
                  </p>
                  <div className="flex gap-2">
                    <Input readOnly value={signupUrl} className="text-xs" />
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={copySignupUrl}
                      title="Copiar link"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </InlineNotice>
            </FormModalSection>
          )}
        </div>

        {/* Rodapé fixo com as ações */}
        <div className="border-t px-6 py-3 flex items-center justify-end gap-2 shrink-0 bg-background">
          <Button variant="outline" onClick={closeAll} disabled={submitting}>
            {signupUrl ? "Fechar" : "Cancelar"}
          </Button>
          {!signupUrl && (
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Registrar usuário
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
